<template>
    <template v-for="row in FieldsStructure">
        <OptionalWrapper :tag="'div'" :active="row.hasNoRowClass ? false : true" :class="`row ${row.class || ''}`"
            v-if="hasVisibleFields(row.fields)">
            <template v-for="field in row.fields">
                <template v-if="field.visible !== undefined ? field.visible : true">
                    <component v-if="field.type === 'inject'" :is="field.classInstance" />
                    <OptionalWrapper v-else :tag="'div'" :active="field.hasNoColumnClass ? false : true"
                        :class="`${field.columnLayoutClasses ? field.columnLayoutClasses : 'col-sm-' + (12 / row.fields.length)} ${field.columnCustomClasses || ''}`">
                        <div
                            :class="`form-group
                            ${field.formGroupClasses ? field.formGroupClasses : ''}
                            ${field.mandatory === true ? 'required' : ''}
                            ${field.htmlName}`">
                            <component :is="(isSelectTag(field) ? field.tag : 'input')"
                                :class="`form-control ${field.classes} ${isInvalid(field)}`"
                                :type="(isSelectTag(field) ? null : field.type || 'text')"
                                :inputmode="field.inputmode || 'text'"
                                :autocomplete="(field.autocomplete || 'off')"
                                :id="field.formId" :placeholder="getFieldLabel(field, FormName)" :name="field.name"
                                :aria-labelledby="`${field.formId}-label`"
                                :value="getValueAttribute(field)"
                                @input="field.events && field.events['input'] ? onInput(field, $event.target) : null"
                                @change="field.events && field.events['change'] ? (oldEventBus.$emit(field.events['change'], $event.target)) : null"
                                @click="field.events && field.events['click'] ? (oldEventBus.$emit(field.events['click'], field.id)) : null"
                                @focus="field.events && field.events['focus'] ? (oldEventBus.$emit(field.events['focus'], field.id)) : null"
                                @blur="oldEventBus.$emit('fieldValidation', { FormName: props.FormName, field: field }) && field.events && field.events['blur'] ? (oldEventBus.$emit(field.events['blur'], field.id)) : null"
                                :readonly="field.readonly"
                                :pattern="getRegexPattern(field)"
                                v-bind="stringAttributesToObjects(field.attributes)"
                                :aria-describedby=" `${field.formId} ${field.formId}-error`"
                                :aria-labeledby=" `${field.formId} ${field.formId}-error`">
                                <template v-if="isSelectTag(field)">
                                    <template v-for="option in (field.filteredOptions || field.options)">
                                        <option :id="option.id" :value="option.htmlValue" :hidden="option.hidden">{{ option.label }}
                                        </option>
                                    </template>
                                </template>
                            </component>
                            <label
                                :id="`${field.formId}-label`"
                                :class="`${field.labelLayoutClasses ? field.labelLayoutClasses : 'form-control-placeholder'} ${field.labelCustomClasses ? field.labelCustomClasses : ''}`"
                                :for="field.formId" v-html="getFieldLabel(field, FormName)">
                            </label>
                            <span class="icon-chevron" v-if="isSelectTag(field)">
                                <SvgHelper :icon="'chevron'" :svgtitle="'chevron'" :decorative="true" />
                            </span>
                            <legend v-if="field.legend" :class="field.legend.classes" v-html="field.legend.text">
                            </legend>
                            <div class="invalid-feedback" role="alert" v-html="fieldErrors[FormName][field.id] || field.error" :id="`${field.formId}-error`">
                            </div>
                        </div>
                    </OptionalWrapper>
                </template>
            </template>
        </OptionalWrapper>
    </template>
</template>
    
<script setup>
import { defineProps, computed, watch, onUpdated, nextTick } from 'vue';
import { stringAttributesToObjects, SvgHelper } from "@/components/helper/util"
import { EventBus as oldEventBus } from "@/scripts/event-bus.js";
import { mapMutations, mapGetters, mapActions } from "@/store/helper/vuex";
import OptionalWrapper from "@/components/helper/OptionalWrapper"
import { isRef, reactive } from "vue"

const props = defineProps({
    FieldsStructure: Object,
    FormName: String,
})

const { updateFormField } = mapActions();

const { formFields, fieldErrors, checkout, vueVariables } = mapGetters();

const pdict = checkout || mapGetters().pdict;

const isCustomerAuthenticated = computed(() => { return vueVariables && vueVariables.value.hasOwnProperty('isCustomerAuthenticated') ? vueVariables.value.isCustomerAuthenticated  : true });

const HKCity = computed(() => { return formFields.value.billing.HKCity });

const getRegexPattern = (field) => {
  switch (field.id) {
    case 'postalCode':
      return pdict.value.postalCodeRegexMap[formFields.value[props.FormName].country];
    default:
      return null;
  }
}

const HKCityWatcher = watch(HKCity, (newValue, oldValue) => {
    if (formFields.value.billing.country === 'HK' && newValue && newValue !== oldValue) {
        updateFormField({
            formName: 'billing',
            fieldName: 'city',
            fieldValue: newValue
        });
    }
});

const onInput = (field, target) => {
    const isCheckbox = isCheckboxTag(field);
    const value = isCheckbox ? target.checked : target.value;
    updateFieldValue(field, value);
    isCheckbox ? updateCheckboxTargetValue(target, value) : null;
    if (field.id === 'address1') {
        oldEventBus.$emit('getAddressSuggestions', value);
    }
}

const updateCheckboxTargetValue = (target, value) => {
    target.checked = !!value;
}

const updateFieldValue = (field, value) => {
    updateFormField({
        formName: props.FormName,
        fieldName: field.id,
        fieldValue: value
    });
}

const isSelectTag = (field) => {
    return field.tag && field.tag === 'select' ? true : false;
}

const isCheckboxTag = (field) => {
    return field.type && field.type === 'checkbox' ? true : false;
}

const getAndUpdateVModelFieldValue = (field, finalValue) => {
    updateFieldValue(field, finalValue);
    return finalValue;
}

const hasNonNullOrUndefinedValue = (value) => {
    return value !== undefined && value !== null;
}

const getValueAttribute = (field, overrideParams) => {
    let formFieldValue;

    if (field.type === 'hidden') {
        formFieldValue = field.value;
    } else if (overrideParams && overrideParams.forceEmpty) {
        formFieldValue = overrideParams.forceEmptyValue;
    } else {
        formFieldValue = formFields.value[props.FormName][field.id];
    }

    // Checking !== undefined because '' is a valid value for input
    // '' in not valid for a select tag
    const isEmptySelect = isSelectTag(field) && formFieldValue === '';
    const isCheckbox = isCheckboxTag(field);

    if (isCheckbox) {
        const checkbox = document.getElementById(field.formId)
        if (checkbox) {
            checkbox.checked = !!formFieldValue;
        }
        return !!formFieldValue;
    }

    if (isEmptySelect) {
        const wantedOptions = field.filteredOptions && field.filteredOptions.length > 0 ? field.filteredOptions : field.options;
        let defaultSelectValue = field.name === 'country' ? getValue(field.defaultValue) : (wantedOptions ? (hasNonNullOrUndefinedValue(wantedOptions[0].htmlValue) ? wantedOptions[0].htmlValue : '') : '');
        return formFieldValue !== defaultSelectValue ? getAndUpdateVModelFieldValue(field, defaultSelectValue) : defaultSelectValue;
    } else {
        return hasNonNullOrUndefinedValue(formFieldValue) ? formFieldValue : getAndUpdateVModelFieldValue(field, hasNonNullOrUndefinedValue(getValue(field.defaultValue)) ? getValue(field.defaultValue) : '')
    }
}

const getValue = (fieldValue) => {
    // Sanity Check, Vue 3 normally unpacks the .value
    if (fieldValue && fieldValue.value && isRef(fieldValue)) {
        return fieldValue.value;
    } else
        return fieldValue
}

const hasVisibleFields = (rowFields) => {
    if (rowFields) {
        const test = rowFields.some((field) => {
            if (getValue(field.visible) === false) {
                return false;
            }
            return true;
        });
        return test;
    }
    return true;
}

const isInvalid = (field) => {
    return fieldErrors.value[props.FormName] ? (fieldErrors.value[props.FormName][field.id] ? 'is-invalid' : '') : '';
}

const getFieldLabel = (field, FormName) => {
    return typeof field.label === 'string' ? field.label : field.label[pdict.value.locale];
}

oldEventBus.$on('clearFormFields', ({ formName, currentDynamicStructure }) => {
    if (props.FormName !== formName)
        return;

    for (const rowName of Object.keys(currentDynamicStructure)) {
        const row = currentDynamicStructure[rowName];
        if (row.fields && row.fields.length > 0) {
            for (const field of row.fields) {
                if (!(isCustomerAuthenticated.value && pdict.value.addressBookAvailable && field.name === 'email') && field.visible !== false && field.type !== 'inject') {
                    let validEmptyValue = getValueAttribute(field, {
                        forceEmpty: true,
                        forceEmptyValue: ''
                    })
                    updateFormField({
                        formName,
                        fieldName: field.name,
                        fieldValue: validEmptyValue
                    })
                }
            }
        }
    }
});

oldEventBus.$on('clearSpecificFormFields', ({ formName, currentDynamicStructure, specificFormFields }) => {
    if (props.FormName !== formName)
        return;

    specificFormFields.forEach((fieldName) => {
        let field = currentDynamicStructure.get(fieldName);
        if (field) {
            let validEmptyValue = getValueAttribute(field, {
                forceEmpty: true,
                forceEmptyValue: ''
            })
            updateFormField({
                formName,
                fieldName: field.name,
                fieldValue: validEmptyValue
            })
        }
    })
});

onUpdated(() => {
    nextTick(() => {    
        let nameFields = document.querySelectorAll('#shippingFirstName, #shippingLastName, #billingFirstName, #billingLastName');
        if (nameFields.length) {
            nameFields.forEach((field) => {
                field.setAttribute('autocorrect', 'off');
            });
        }
    });
});

</script>